import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Button,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { accessApi, systemApi } from '../../services/accessApi';
import * as XLSX from 'xlsx';

export default function AccessMatrix() {
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState([]);
  const [systems, setSystems] = useState([]);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matrixResponse, systemsResponse] = await Promise.all([
        accessApi.getMatrix({ status: 'Active' }),
        systemApi.getAll({ isActive: true })
      ]);

      setMatrix(matrixResponse.data.data.matrix);
      setSystems(systemsResponse.data.data);
    } catch (error) {
      console.error('Error loading access matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessToggle = (employeeId, systemName, currentValue) => {
    const key = `${employeeId}-${systemName}`;
    setChanges({
      ...changes,
      [key]: {
        ...changes[key],
        hasAccess: !currentValue
      }
    });
  };

  const handleRoleChange = (employeeId, systemName, role) => {
    const key = `${employeeId}-${systemName}`;
    setChanges({
      ...changes,
      [key]: {
        ...changes[key],
        role
      }
    });
  };

  const handleSaveChanges = async () => {
    try {
      // Process each change
      for (const [key, change] of Object.entries(changes)) {
        const [employeeId, systemName] = key.split('-');
        const system = systems.find(s => s.name === systemName);

        if (change.hasAccess !== undefined) {
          if (change.hasAccess) {
            await accessApi.grant({
              employeeId,
              systemId: system._id,
              role: change.role || 'User'
            });
          } else {
            await accessApi.revoke({
              employeeId,
              systemId: system._id
            });
          }
        } else if (change.role) {
          await accessApi.grant({
            employeeId,
            systemId: system._id,
            role: change.role
          });
        }
      }

      setChanges({});
      loadData();
      alert('Changes saved successfully');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes');
    }
  };

  const handleExportToExcel = () => {
    // Transform matrix data for Excel
    const excelData = matrix.map(item => {
      const row = {
        Name: item.employee.name,
        Email: item.employee.email,
        Department: item.employee.department,
        'Job Title': item.employee.jobTitle
      };

      systems.forEach(system => {
        const access = item.systems[system.name];
        row[`${system.displayName} - Access`] = access.hasAccess ? 'Yes' : 'No';
        row[`${system.displayName} - Role`] = access.role || 'N/A';
        if (access.licenseType) {
          row[`${system.displayName} - License`] = access.licenseType;
        }
      });

      return row;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = 50;
    const colWidths = Object.keys(excelData[0] || {}).map(key => ({
      wch: Math.min(Math.max(key.length, 10), maxWidth)
    }));
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Access Matrix');

    // Generate and download file
    XLSX.writeFile(wb, `access-matrix-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getAccessValue = (employeeId, systemName) => {
    const key = `${employeeId}-${systemName}`;
    const employee = matrix.find(m => m.employee._id === employeeId);

    if (changes[key]?.hasAccess !== undefined) {
      return changes[key].hasAccess;
    }

    return employee?.systems[systemName]?.hasAccess || false;
  };

  const getRoleValue = (employeeId, systemName) => {
    const key = `${employeeId}-${systemName}`;
    const employee = matrix.find(m => m.employee._id === employeeId);

    if (changes[key]?.role) {
      return changes[key].role;
    }

    return employee?.systems[systemName]?.role || '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Access Matrix - Active Employees</Typography>
        <Box>
          {Object.keys(changes).length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              sx={{ mr: 2 }}
            >
              Save Changes ({Object.keys(changes).length})
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 150, position: 'sticky', left: 0, background: '#fff', zIndex: 3 }}>
                Employee
              </TableCell>
              <TableCell sx={{ minWidth: 120, position: 'sticky', left: 150, background: '#fff', zIndex: 3 }}>
                Department
              </TableCell>
              {systems.map((system) => (
                <TableCell key={system._id} align="center" sx={{ minWidth: 180 }}>
                  <Tooltip title={system.description || system.displayName}>
                    <span>{system.displayName}</span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.map((item) => (
              <TableRow key={item.employee._id}>
                <TableCell sx={{ position: 'sticky', left: 0, background: '#fff', zIndex: 2 }}>
                  <div>
                    <Typography variant="body2" fontWeight="bold">
                      {item.employee.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.employee.email}
                    </Typography>
                  </div>
                </TableCell>
                <TableCell sx={{ position: 'sticky', left: 150, background: '#fff', zIndex: 2 }}>
                  {item.employee.department}
                </TableCell>
                {systems.map((system) => {
                  const hasAccess = getAccessValue(item.employee._id, system.name);
                  const role = getRoleValue(item.employee._id, system.name);

                  return (
                    <TableCell key={system._id} align="center">
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        <Checkbox
                          checked={hasAccess}
                          onChange={() => handleAccessToggle(item.employee._id, system.name, hasAccess)}
                          size="small"
                        />
                        {hasAccess && system.availableRoles.length > 0 && (
                          <Select
                            size="small"
                            value={role}
                            onChange={(e) => handleRoleChange(item.employee._id, system.name, e.target.value)}
                            sx={{ minWidth: 100, fontSize: '0.75rem' }}
                          >
                            {system.availableRoles.map((r) => (
                              <MenuItem key={r} value={r} sx={{ fontSize: '0.75rem' }}>
                                {r}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
